import {
    useState,
    useRef,
    useEffect,
    useCallback,
    useMemo,
    useLayoutEffect,
    type CSSProperties,
    type FC,
    type ReactNode,
    type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { LuChevronDown, LuSearch, LuCheck } from 'react-icons/lu';
import './Select.css';

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
    /** Linha de apoio embaixo do rótulo. Usar só quando a escolha depende dela. */
    meta?: string;
    /** Ícone à esquerda da opção. */
    icon?: ReactNode;
    /** Iniciais num quadrado — atalho para registro (pessoa, equipe, empresa). */
    avatar?: string;
    /** Ponto de cor à esquerda — para status. Recebe a cor pronta. */
    dot?: string;
    /** Seção a que a opção pertence. Opções sem `group` vêm antes das agrupadas. */
    group?: string;
}

interface SelectProps {
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    /**
     * O tamanho diz o PAPEL, e o papel decide o raio — é o que organiza a
     * tabela do `padrao-de-tela.md` §2 (campo de texto 8px, botão/filtro 6px):
     *
     * - `md` (34px, raio 8) — campo de formulário. Nivela com `.rp-input`.
     * - `sm` (32px, raio 8) — campo compacto, como os do painel do chat.
     * - `control` (32px, raio 6) — filtro e seletor de cabeçalho, ao lado do
     *   MonthPicker e das abas.
     * - `pill` (26px, raio 6) — controle dentro de texto corrido, como o
     *   escopo no compositor do assistente e o "por página" do rodapé.
     *
     * `sm` e `control` têm a mesma altura de propósito: o que muda entre eles
     * é o papel, não o tamanho.
     */
    size?: 'sm' | 'md' | 'control' | 'pill';
    /** A partir de quantas opções aparece o campo de filtro. */
    searchFrom?: number;
    /** Ícone fixo no gatilho, à esquerda do rótulo. */
    icon?: ReactNode;
    /** Mostra no gatilho o acessório da opção escolhida (ícone, avatar, ponto). */
    showOptionAccessory?: boolean;
    /** Ocupa a largura inteira do pai. Ligado por padrão. */
    block?: boolean;
    /**
     * Abre para cima sempre. Para controle que vive no rodapé de um bloco —
     * o compositor do assistente — onde a lista descendo cobriria o que vem
     * depois. Sem isto, a lista só sobe quando não cabe embaixo.
     */
    dropUp?: boolean;
    /**
     * Desenha a lista no `body`, com posição fixa. Para Select que vive dentro
     * de célula de tabela: `.lk-cell` e `.lk-scroll` cortam o que transborda,
     * e a lista abria recortada na altura da linha. Os tokens do contexto
     * (`.rp`, `.ch-painel`…) são copiados do gatilho, então a superfície não
     * muda por sair da árvore.
     */
    portal?: boolean;
    hasError?: boolean;
    id?: string;
    className?: string;
    ariaLabel?: string;
    /** Tooltip do gatilho — serve para explicar por que está desabilitado. */
    title?: string;
    /** Texto do popover quando não há nenhuma opção. */
    emptyText?: string;
}

const ALTURA_OPCAO = 34;
const TOKENS = ['ink', 'muted', 'faint', 'hairline', 'hairline-soft', 'track', 'surface', 'blue', 'bad', 'ring', 'sombra'];

/**
 * Iniciais para o acessório `avatar` — primeiro e último nome, ou as duas
 * primeiras letras. Vinha de dentro do `EntitySelect`; virou utilitário
 * porque o avatar agora é acessório de qualquer opção.
 */
export const iniciaisDe = (nome: string) => {
    const partes = nome.trim().split(/\s+/);
    if (partes.length > 1) return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
    return nome.substring(0, 2).toUpperCase();
};

/**
 * Select único do app — substitui o `<select>` nativo, o `CustomSelect`, o
 * `EntitySelect` e a pílula de escopo do assistente.
 *
 * Proposta de 2026-09-12 (docs/designsystem/propostas/selects.html), direção C:
 * a caixa, o popover, o espaçamento e a marca de selecionado são sempre os
 * mesmos; ícone, avatar, ponto de cor, linha de apoio e agrupamento são
 * acessórios que a tela liga ou não liga. Sem hover — regra do produto.
 *
 * O nativo saiu porque `appearance:none` veste a caixa e não alcança a lista:
 * ela é desenhada pelo sistema operacional e abre branca no tema escuro.
 */
const Select: FC<SelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Selecione',
    disabled = false,
    size = 'md',
    searchFrom = 8,
    icon,
    showOptionAccessory = false,
    block = true,
    dropUp = false,
    portal = false,
    hasError = false,
    id,
    className = '',
    ariaLabel,
    title,
    emptyText = 'Nenhuma opção disponível',
}) => {
    const [aberto, setAberto] = useState(false);
    const [termo, setTermo] = useState('');
    const [marcado, setMarcado] = useState(-1);
    const [paraCima, setParaCima] = useState(false);

    const raizRef = useRef<HTMLDivElement>(null);
    const gatilhoRef = useRef<HTMLButtonElement>(null);
    const listaRef = useRef<HTMLDivElement>(null);
    const buscaRef = useRef<HTMLInputElement>(null);
    const popRef = useRef<HTMLDivElement>(null);
    const [flutua, setFlutua] = useState<CSSProperties | null>(null);
    /* Typeahead de quem não tem campo de busca: o nativo faz isso e seria uma
       perda de comportamento não fazer. */
    const digitado = useRef({ texto: '', quando: 0 });

    const escolhida = options.find(o => o.value === value) ?? null;
    const temBusca = options.length >= searchFrom;

    const visiveis = useMemo(() => {
        const t = termo.trim().toLowerCase();
        if (!t) return options;
        return options.filter(
            o => o.label.toLowerCase().includes(t) || (o.meta ?? '').toLowerCase().includes(t),
        );
    }, [options, termo]);

    /* Agrupa preservando a ordem de chegada: as soltas primeiro, depois cada
       seção na ordem em que apareceu. Sem `group` em nenhuma, não desenha
       cabeçalho nenhum. */
    const blocos = useMemo(() => {
        const semGrupo = visiveis.filter(o => !o.group);
        const nomes: string[] = [];
        visiveis.forEach(o => {
            if (o.group && !nomes.includes(o.group)) nomes.push(o.group);
        });
        const saida: { titulo: string | null; itens: SelectOption[] }[] = [];
        if (semGrupo.length) saida.push({ titulo: null, itens: semGrupo });
        nomes.forEach(n => saida.push({ titulo: n, itens: visiveis.filter(o => o.group === n) }));
        return saida;
    }, [visiveis]);

    /* A ordem achatada é a que o teclado percorre — tem que bater com a ordem
       desenhada, senão a seta pula. */
    const naOrdem = useMemo(() => blocos.flatMap(b => b.itens), [blocos]);
    const selecionaveis = naOrdem.filter(o => !o.disabled);

    const fechar = useCallback((devolveFoco = true) => {
        setAberto(false);
        setTermo('');
        setMarcado(-1);
        if (devolveFoco) gatilhoRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!aberto) return;
        const foraDaqui = (e: MouseEvent) => {
            const alvo = e.target as Node;
            if (raizRef.current?.contains(alvo) || popRef.current?.contains(alvo)) return;
            fechar(false);
        };
        document.addEventListener('mousedown', foraDaqui);
        return () => document.removeEventListener('mousedown', foraDaqui);
    }, [aberto, fechar]);

    /* Decide para que lado o popover abre antes de pintar: dentro de painel a
       lista ia nascer fora da tela e o usuário nem via que abriu. */
    useEffect(() => {
        if (!aberto) return;
        if (dropUp) { setParaCima(true); return; }
        const caixa = gatilhoRef.current?.getBoundingClientRect();
        if (!caixa) return;
        const altura = Math.min(naOrdem.length, 8) * ALTURA_OPCAO + (temBusca ? 36 : 0) + 14;
        setParaCima(caixa.bottom + altura > window.innerHeight && caixa.top > altura);
    }, [aberto, naOrdem.length, temBusca, dropUp]);

    /* No modo portal a lista acompanha o gatilho: a tabela rola por baixo
       dela, e sem isto ela ficaria parada no ar. */
    useLayoutEffect(() => {
        if (!aberto || !portal) { setFlutua(null); return; }
        const tokens: Record<string, string> = {};
        if (raizRef.current) {
            const estilo = getComputedStyle(raizRef.current);
            TOKENS.forEach(t => { tokens[`--ds-${t}`] = estilo.getPropertyValue(`--ds-${t}`); });
        }
        const posicionar = () => {
            const caixa = gatilhoRef.current?.getBoundingClientRect();
            if (!caixa) return;
            const largura = Math.max(popRef.current?.offsetWidth ?? 0, caixa.width);
            setFlutua({
                ...tokens,
                left: Math.max(8, Math.min(caixa.left, window.innerWidth - largura - 8)),
                minWidth: caixa.width,
                ...(paraCima
                    ? { bottom: window.innerHeight - caixa.top + 6 }
                    : { top: caixa.bottom + 6 }),
            } as CSSProperties);
        };
        posicionar();
        window.addEventListener('scroll', posicionar, true);
        window.addEventListener('resize', posicionar);
        return () => {
            window.removeEventListener('scroll', posicionar, true);
            window.removeEventListener('resize', posicionar);
        };
    }, [aberto, portal, paraCima]);

    useEffect(() => {
        if (aberto && temBusca) buscaRef.current?.focus();
    }, [aberto, temBusca]);

    useEffect(() => {
        if (!aberto || marcado < 0) return;
        listaRef.current
            ?.querySelector(`[data-indice="${marcado}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, [aberto, marcado]);

    const abrir = () => {
        if (disabled) return;
        const atual = naOrdem.findIndex(o => o.value === value && !o.disabled);
        setMarcado(atual >= 0 ? atual : naOrdem.findIndex(o => !o.disabled));
        setAberto(true);
    };

    const escolher = (opcao: SelectOption) => {
        if (opcao.disabled) return;
        onChange(opcao.value);
        fechar();
    };

    const andar = (passo: number) => {
        if (!selecionaveis.length) return;
        const atual = marcado;
        let i = atual;
        for (let n = 0; n < naOrdem.length; n++) {
            i = (i + passo + naOrdem.length) % naOrdem.length;
            if (!naOrdem[i].disabled) break;
        }
        setMarcado(i);
    };

    const porDigitacao = (letra: string) => {
        const agora = Date.now();
        const d = digitado.current;
        d.texto = agora - d.quando > 700 ? letra : d.texto + letra;
        d.quando = agora;
        const achou = naOrdem.findIndex(
            o => !o.disabled && o.label.toLowerCase().startsWith(d.texto.toLowerCase()),
        );
        if (achou >= 0) setMarcado(achou);
    };

    const noTeclado = (e: KeyboardEvent) => {
        if (disabled) return;

        if (!aberto) {
            if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
                e.preventDefault();
                abrir();
            }
            return;
        }

        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                fechar();
                break;
            case 'ArrowDown':
                e.preventDefault();
                andar(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                andar(-1);
                break;
            case 'Home':
                e.preventDefault();
                setMarcado(naOrdem.findIndex(o => !o.disabled));
                break;
            case 'End':
                e.preventDefault();
                for (let i = naOrdem.length - 1; i >= 0; i--) {
                    if (!naOrdem[i].disabled) { setMarcado(i); break; }
                }
                break;
            case 'Enter':
            case 'Tab':
                if (marcado >= 0 && naOrdem[marcado]) {
                    e.preventDefault();
                    escolher(naOrdem[marcado]);
                } else if (e.key === 'Enter') {
                    e.preventDefault();
                }
                break;
            default:
                if (!temBusca && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
                    porDigitacao(e.key);
                }
        }
    };

    const acessorio = (o: SelectOption) => {
        if (o.dot) return <span className="ds-dot" style={{ backgroundColor: o.dot }} />;
        if (o.avatar) return <span className="ds-avatar">{o.avatar}</span>;
        if (o.icon) return <span className="ds-ic">{o.icon}</span>;
        return null;
    };

    const pop = (
        <div
            ref={popRef}
            className={`ds-pop ${paraCima ? 'is-up' : ''} ${portal ? 'is-flutuante' : ''}`}
            style={flutua ?? undefined}
        >
            {temBusca && (
                <div className="ds-busca">
                    <LuSearch />
                    <input
                        ref={buscaRef}
                        type="text"
                        value={termo}
                        placeholder="Filtrar"
                        onChange={e => { setTermo(e.target.value); setMarcado(0); }}
                        onKeyDown={noTeclado}
                    />
                </div>
            )}

            <div className="ds-lista" ref={listaRef} role="listbox">
                {naOrdem.length === 0 ? (
                    <p className="ds-vazio">{options.length === 0 ? emptyText : 'Nada encontrado.'}</p>
                ) : (
                    blocos.map((bloco, bi) => (
                        <div className="ds-grupo" key={bloco.titulo ?? `solto-${bi}`}>
                            {bloco.titulo && <p className="ds-grupo-t">{bloco.titulo}</p>}
                            {bloco.itens.map(o => {
                                const indice = naOrdem.indexOf(o);
                                return (
                                    <button
                                        key={o.value}
                                        type="button"
                                        data-indice={indice}
                                        role="option"
                                        aria-selected={o.value === value}
                                        disabled={o.disabled}
                                        className={[
                                            'ds-opcao',
                                            o.value === value ? 'is-active' : '',
                                            indice === marcado ? 'is-marcada' : '',
                                            o.meta ? 'ds-duas' : '',
                                        ].filter(Boolean).join(' ')}
                                        onClick={() => escolher(o)}
                                        onMouseMove={() => setMarcado(indice)}
                                    >
                                        {acessorio(o)}
                                        <span className="ds-opcao-txt">
                                            <span className="ds-opcao-nome">{o.label}</span>
                                            {o.meta && <span className="ds-opcao-meta">{o.meta}</span>}
                                        </span>
                                        {o.value === value && <LuCheck className="ds-check" />}
                                    </button>
                                );
                            })}
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    const classes = [
        'ds-select',
        `ds-${size}`,
        block ? 'ds-block' : '',
        disabled ? 'is-disabled' : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <div className={classes} ref={raizRef}>
            <button
                type="button"
                id={id}
                ref={gatilhoRef}
                className={`ds-trigger ${aberto ? 'is-open' : ''} ${hasError ? 'has-error' : ''}`}
                onClick={() => (aberto ? fechar() : abrir())}
                onKeyDown={noTeclado}
                disabled={disabled}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={aberto}
                aria-label={ariaLabel}
                title={title}
            >
                {icon && <span className="ds-ic ds-ic-fixo">{icon}</span>}
                {showOptionAccessory && escolhida && acessorio(escolhida)}
                <span className={`ds-valor ${escolhida ? '' : 'is-placeholder'}`}>
                    {escolhida ? escolhida.label : placeholder}
                </span>
                <LuChevronDown className="ds-caret" />
            </button>

            {aberto && (portal ? createPortal(pop, document.body) : pop)}
        </div>
    );
};

export default Select;
